import React, { useEffect, useRef } from "react";

import {
  ADSENSE_DEFAULT_SLOT_ID,
  ADSENSE_PUBLISHER_CLIENT,
} from "../../config/adsense";
import { ensureAdsenseScript } from "../../utils/loadAdsenseScript";

interface AdComponentProps {
  /** ID numérico da unidade no AdSense. */
  slot?: string;
  format?: "auto" | "fluid" | "rectangle";
  style?: React.CSSProperties;
  className?: string;
}

const AdComponent: React.FC<AdComponentProps> = ({
  slot,
  format = "auto",
  style = {},
  className,
}) => {
  const insRef = useRef<HTMLModElement>(null);
  const pushedRef = useRef(false);
  const slotId = (slot || ADSENSE_DEFAULT_SLOT_ID).trim();

  useEffect(() => {
    const ins = insRef.current;
    if (!ins || !slotId) {
      return undefined;
    }

    let cancelled = false;
    let ro: ResizeObserver | null = null;
    let timeoutId = 0;

    const tryFill = () => {
      if (cancelled || pushedRef.current || !insRef.current) return;
      const w = insRef.current.offsetWidth;
      if (w < 48) return;

      try {
        const wAds = window as Window & { adsbygoogle?: unknown[] };
        wAds.adsbygoogle = wAds.adsbygoogle || [];
        wAds.adsbygoogle.push({});
        pushedRef.current = true;
      } catch (e) {
        console.error("AdSense push:", e);
      }
    };

    (async () => {
      try {
        await ensureAdsenseScript(ADSENSE_PUBLISHER_CLIENT);
      } catch {
        return;
      }
      if (cancelled) return;

      tryFill();
      const parent = insRef.current?.parentElement;
      if (!pushedRef.current && parent) {
        ro = new ResizeObserver(() => tryFill());
        ro.observe(parent);
      }
      timeoutId = window.setTimeout(tryFill, 800);
    })();

    return () => {
      cancelled = true;
      ro?.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
      pushedRef.current = false;
    };
  }, [slotId]);

  if (!ADSENSE_PUBLISHER_CLIENT || !slotId) {
    return null;
  }

  return (
    <div
      className={className}
      style={{
        margin: "16px 0",
        textAlign: "center",
        minHeight: 90,
        ...style,
      }}
    >
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", width: "100%", maxWidth: "100%", ...style }}
        data-ad-client={ADSENSE_PUBLISHER_CLIENT}
        data-ad-slot={slotId}
        data-ad-format={format}
        data-full-width-responsive="true"
      />
    </div>
  );
};

export default AdComponent;
